import React, { useState } from 'react';
import { IS_DEV } from '@/lib/env';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { UserCheck, RotateCcw, Bug } from 'lucide-react';

export const IMPERSONATE_STORAGE_KEY = 'impersonate_emirates_id';

export function getImpersonatedEmiratesId(): string | null {
  if (!IS_DEV) return null;
  if (typeof window === 'undefined' || !window.sessionStorage) return null;
  const value = window.sessionStorage.getItem(IMPERSONATE_STORAGE_KEY);
  return value ? value.trim() : null;
}

export function setImpersonatedEmiratesId(emiratesId: string): void {
  if (typeof window === 'undefined' || !window.sessionStorage) return;
  const trimmed = emiratesId.trim();
  if (trimmed) {
    window.sessionStorage.setItem(IMPERSONATE_STORAGE_KEY, trimmed);
  } else {
    window.sessionStorage.removeItem(IMPERSONATE_STORAGE_KEY);
  }
}

export function clearImpersonatedEmiratesId(): void {
  if (typeof window === 'undefined' || !window.sessionStorage) return;
  window.sessionStorage.removeItem(IMPERSONATE_STORAGE_KEY);
}

export default function DebugBar() {
  if (!IS_DEV) {
    return null;
  }

  const activeEmiratesId = getImpersonatedEmiratesId();
  const [emiratesIdInput, setEmiratesIdInput] = useState(activeEmiratesId ?? '');

  const handleApply = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const trimmed = emiratesIdInput.trim();
    if (!trimmed) return;
    setImpersonatedEmiratesId(trimmed);
    window.location.reload();
  };

  const handleClear = () => {
    clearImpersonatedEmiratesId();
    setEmiratesIdInput('');
    window.location.reload();
  };

  return (
    <div
      dir="ltr"
      className="bg-slate-900 text-slate-100 border-b border-slate-700 px-4 py-2 text-xs sm:text-sm sticky top-0 z-50 shadow-md"
    >
      <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5 font-semibold text-amber-400">
            <Bug className="w-4 h-4" />
            <span>DEV MODE</span>
          </div>
          <Badge
            variant="outline"
            className={
              activeEmiratesId
                ? 'bg-amber-500/20 text-amber-300 border-amber-500/40 text-[11px]'
                : 'bg-slate-800 text-slate-400 border-slate-700 text-[11px]'
            }
          >
            {activeEmiratesId ? `Impersonating: ${activeEmiratesId}` : 'No Impersonation'}
          </Badge>
        </div>

        <form onSubmit={handleApply} className="flex items-center gap-2 flex-1 sm:flex-initial sm:min-w-[400px]">
          <Input
            type="text"
            placeholder="Impersonate Emirates ID (e.g. 784199040973986)"
            value={emiratesIdInput}
            onChange={(e) => setEmiratesIdInput(e.target.value)}
            className="h-8 bg-slate-800 border-slate-700 text-slate-100 placeholder:text-slate-500 focus-visible:ring-amber-400 text-xs"
          />
          <Button
            type="submit"
            size="sm"
            variant="default"
            disabled={!emiratesIdInput.trim() || emiratesIdInput.trim() === activeEmiratesId}
            className="h-8 px-3 bg-amber-500 hover:bg-amber-600 text-slate-950 font-medium text-xs whitespace-nowrap cursor-pointer"
          >
            <UserCheck className="w-3.5 h-3.5 mr-1" />
            Apply
          </Button>
          {(activeEmiratesId || emiratesIdInput) && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={handleClear}
              className="h-8 px-2.5 bg-slate-800 border-slate-700 hover:bg-slate-700 text-slate-300 text-xs whitespace-nowrap cursor-pointer"
              title="Clear Impersonation"
            >
              <RotateCcw className="w-3.5 h-3.5 mr-1" />
              Clear
            </Button>
          )}
        </form>
      </div>
    </div>
  );
}
